import { defineStore } from 'pinia'
import { reactive, ref, watch, computed } from 'vue'
import templates from '@/data/resumeFormTemplates'
import { debounce, get, set } from 'lodash'
import { getParserText } from '@/lib/formatters/parserTextFormatter'
import { updateResume } from '@/api/v4/resume'
import { snackBarStore } from './snackBar'
import { usePowerEditStore } from './powerEdit'
import {
  useGlobalListeners,
  GlobalEvents,
} from '@/composables/useGlobalListeners'
import { generateDocx } from '@/lib/docx'
import { resumeSummary } from '@/api/v4/generators'
import { Resume } from '@/types/resume/v1'

export const useStructuredResumeStore = defineStore('structuredResume', () => {
  const { registerListener } = useGlobalListeners()
  const snackBar = snackBarStore()
  const resumeRecord = ref()
  const resumeName = ref<string>('')
  const resume = reactive<any>({})
  const isPowerEdit = ref(true)
  const lastSave = ref()
  const isSaving = ref(false)
  const isGeneratingSummary = ref(false)
  const focusedId = ref<null | string>(null)
  const focusedSection = ref<null | string>(null)

  const setPowerEdit = (value: boolean) => {
    isPowerEdit.value = value
  }

  const removeSection = (section: string) => {
    if (!resume[section]) return
    if (Array.isArray(resume[section])) {
      resume[section] = []
    }
  }

  const addSectionItem = (section: string) => {
    if (!resume[section]) resume[section] = []
    if (section === 'skills' || section === 'interests') return
    const clone = structuredClone(templates[section])
    clone.uuid = crypto.randomUUID()
    resume[section].push(clone)
    return clone.uuid
  }

  const removeSectionItem = (section: string, index: number) => {
    const array = get(resume, section)
    array.splice(index, 1)
  }

  const addUUIDs = (resume: any) => {
    const sections = [
      'work',
      'education',
      'projects',
      'volunteer',
      'publications',
      'certificates',
      'awards',
      'references',
      'skills',
      'categorizedSkills',
      'languages',
      'interests',
      'additional',
      'leadership',
    ]
    sections.forEach(section => {
      if (!resume[section]) return
      resume[section].forEach(item => {
        if (item && !item.uuid) item.uuid = crypto.randomUUID()
      })
    })
  }

  const setResume = (newResume: any) => {
    cleanHighlights(newResume)
    addUUIDs(newResume)
    Object.assign(resume, newResume)
  }

  watch(resumeRecord, newResume => {
    setResume(newResume?.data ?? {})
    lastSave.value = newResume?.updatedAt
    resumeName.value = newResume?.name
  })

  const addCoreSkill = (skill: string) => {
    if (!resume.skills?.length) resume.skills = []
    if (resume.skills.find(s => s.name?.toLowerCase() === skill.toLowerCase()))
      return
    resume.skills.push({
      name: skill,
      uuid: crypto.randomUUID(),
    })
  }

  const addSkillToCategory = (categoryIndex: number, skill: string) => {
    resume.categorizedSkills[categoryIndex].skills.push({
      name: skill,
      uuid: crypto.randomUUID(),
    })
  }

  const removeCoreSkill = (skill: string) => {
    if (!resume.skills?.length) return
    const index = resume.skills.findIndex(
      s => s.name?.toLowerCase() === skill.toLowerCase()
    )
    if (index === -1) return
    resume.skills.splice(index, 1)
  }

  const removeEducationMinor = (educationIndex: number, minorIndex: number) => {
    if (!resume.education[educationIndex].minors?.length) return
    resume.education[educationIndex].minors.splice(minorIndex, 1)
  }

  const removeEducationCourse = (
    educationIndex: number,
    courseIndex: number
  ) => {
    if (!resume.education[educationIndex].courses?.length) return
    resume.education[educationIndex].courses.splice(courseIndex, 1)
  }

  const addEducationMinor = (educationIndex: number, prevIndex: number) => {
    if (!resume.education[educationIndex].minors?.length)
      resume.education[educationIndex].minors = []
    resume.education[educationIndex].minors.splice(
      prevIndex + 1,
      0,
      'New minor...'
    )
  }

  const addEducationCourse = (educationIndex: number, prevIndex: number) => {
    if (!resume.education[educationIndex].courses?.length)
      resume.education[educationIndex].courses = []
    resume.education[educationIndex].courses.splice(
      prevIndex + 1,
      0,
      'New course...'
    )
  }

  const addInterest = (interest: string) => {
    if (!resume.interests?.length) resume.interests = []
    resume.interests.push({
      name: interest,
    })
  }

  const addAdditionalItem = (item: string) => {
    if (!resume.additional?.length) resume.additional = []
    resume.additional.push({
      description: item,
    })
  }

  const addLeadershipItem = (item: string) => {
    if (!resume.leadership?.length) resume.leadership = []
    resume.leadership.push({
      description: item,
    })
  }

  const cleanSkills = () => {
    resume.skills = resume.skills.filter(skill => skill.name?.trim())
  }

  const cleanInterests = () => {
    resume.interests = resume.interests.filter(interest =>
      interest.name?.trim()
    )
  }

  const cleanAdditional = () => {
    resume.additional = resume.additional.filter(additional =>
      additional.description?.trim()
    )
  }

  const cleanLeadership = () => {
    resume.leadership = resume.leadership.filter(leadership =>
      leadership.description?.trim()
    )
  }

  const cleanHighlights = resume => {
    const sections = ['work', 'projects', 'volunteer']
    sections.forEach(section => {
      if (resume[section]) {
        resume[section].forEach(item => {
          if (item.highlights) {
            item.highlights = item.highlights.filter(
              highlight => highlight && highlight.trim()
            )
          }
        })
      }
    })
  }

  const overwriteResume = newResume => {
    Object.keys(resume).map(key => delete resume[key])
    const fixedResume = applyDataFixes(newResume)
    Object.assign(resume, fixedResume)
  }

  /**
   * Since using migrations to fix the JSON resume data
   * is not feasible, we need to apply the fixes here.
   *
   * Note: this should be removed once the data is fixed.
   *
   * @param resume {Resume.Schema}
   * @returns {Resume.Schema}
   */
  const applyDataFixes = (resume: Resume.Schema): Resume.Schema => {
    if (resume.education) {
      resume.education.forEach((education: Resume.Education) => {
        if (!education.minors) {
          education.minors = []
        }
      })
    }

    if (
      Array.isArray(resume.basics.location) &&
      resume.basics.location.length === 0
    ) {
      resume.basics.location = {
        address: '',
        postalCode: '',
        city: '',
        countryCode: '',
        region: '',
      }
    }

    return resume
  }

  const initialLoad = ref(true)

  const stopWatchingResume = watch(
    resume,
    debounce(
      resume => {
        if (initialLoad.value) return (initialLoad.value = false)
        saveResume()
      },
      5000,
      { leading: true }
    )
  )

  const saveResume = async () => {
    isSaving.value = true
    const { data, error } = await updateResume(resumeRecord.value?.id, {
      data: resume,
    })
    if (error) {
      isSaving.value = false
      return snackBar.showErrorSnackBar('Error saving resume')
    }
    resumeName.value = data.name
    usePowerEditStore().refetchReport()
    lastSave.value = new Date(Date.now()).toISOString()
    isSaving.value = false
  }

  const isBase = computed(() => resumeRecord.value?.isBase ?? false)
  const parserText = computed(() => getParserText(resumeRecord.value?.parserInfo))

  const inlineEditListener = (e: any) => {
    let { path, value } = e.detail
    if (!path?.startsWith('resume')) return
    const targetPath = path.split('.').slice(1)
    if (
      Number.isInteger(Number(targetPath[targetPath.length - 1])) &&
      !value?.trim()
    ) {
      return
    }
    set(resume, targetPath, value)
  }

  const deleteArrayItem = (path: string, index: number) => {
    const targetPath = path.split('.').slice(1)
    const array = get(resume, targetPath)
    array.splice(index, 1)
  }

  const registerInlineEditor = () => {
    registerListener(GlobalEvents.INLINE_EDIT, inlineEditListener)
  }

  const deRegisterInlineEditor = () => {
    window.removeEventListener(GlobalEvents.INLINE_EDIT, inlineEditListener)
  }

  const resetStore = () => {
    initialLoad.value = true
    resumeRecord.value = null
    Object.keys(resume).map(key => delete resume[key])
  }

  const downloadDocx = documentName => {
    const doc = generateDocx(resume, documentName, resumeRecord.value)
  }

  const generateResumeSummary = async () => {
    isGeneratingSummary.value = true
    const { data, error } = await resumeSummary(resumeRecord.value?.id)
    isGeneratingSummary.value = false
    if (error) return snackBar.showErrorSnackBar('Error generating summary')
    if (data && data.summary) {
      resume.basics.summary = data.summary
    }
    return
  }

  return {
    resume,
    resumeName,
    removeSection,
    addSectionItem,
    removeSectionItem,
    setResume,
    overwriteResume,
    resumeRecord,
    isBase,
    isPowerEdit,
    setPowerEdit,
    resetStore,
    saveResume,
    lastSave,
    isSaving,
    registerInlineEditor,
    deRegisterInlineEditor,
    addCoreSkill,
    addSkillToCategory,
    removeCoreSkill,
    addInterest,
    addAdditionalItem,
    cleanAdditional,
    addLeadershipItem,
    cleanLeadership,
    cleanSkills,
    cleanInterests,
    deleteArrayItem,
    focusedId,
    focusedSection,
    downloadDocx,
    generateResumeSummary,
    isGeneratingSummary,
    removeEducationMinor,
    removeEducationCourse,
    addEducationMinor,
    addEducationCourse,
    parserText,
  }
})
